import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractTransactionDualApprovalService } from '../abstraction/abstract.transaction-dual-approval.service';
import { IDecisionedNotificationCount } from '../../interfaces/transaction-dual-approval/decisioned-notification-count.interface';
import { IDecisionedNotificationCountByTransactionType } from '../../interfaces/transaction-dual-approval/decisioned-notification-count-by-transaction-type.interface';
import { IDualApprovalDecision } from '../../interfaces/transaction-dual-approval/dual-approval-decision-request.interface';
import {
  IDualApprovalRequiredRequest,
  IDualApprovalRequiredResponse,
} from '../../interfaces/transaction-dual-approval/dual-approval-required.interface';
import { IFedNowTransactionDualApprovalSummary } from '../../interfaces/transaction-dual-approval/dual-approval-summary.interface';
import { IPendingDualApprovalCount } from '../../interfaces/transaction-dual-approval/pending-dual-approval-count.interface';
import { IUnfinishedDualApprovalCount } from '../../interfaces/transaction-dual-approval/unfinished-dual-approval-count.interface';
import { ITableServerFilter } from 'src/app/common/interfaces/table/table-server-filter.inteface';
import { ITableServerResponseFilter } from 'src/app/common/interfaces/table/table-server-filter-response.interface';
import { IWebMessage } from 'src/app/common/interfaces/webmessage-interface';

/**
 * HTTP implementation of {@link AbstractTransactionDualApprovalService}.
 * Calls the backend API to handle the FedNow Transaction Dual Approval workflow.
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionDualApprovalService extends AbstractTransactionDualApprovalService {
  controllerUrl = `/manager/fednow/transactiondualapproval`;
  urlGetUnfinishedFedNowTransactionApprovalCount = `${environment.apiUrl}${this.controllerUrl}/unfinishedCount`;
  urlGetUnfinishedRtpTransactionApprovalCount = `${environment.apiUrl}/manager/rtp/transactiondualapproval/unfinishedCount`;
  urlGetPendingFedNowTransactionApprovalCount = `${environment.apiUrl}${this.controllerUrl}/pendingCount`;
  urlDecisionedNotificationCountsByStatus = `${environment.apiUrl}${this.controllerUrl}/decisionedNotificationCountsByStatus`;
  urlDecisionedNotificationCountsByTransactionType = `${environment.apiUrl}${this.controllerUrl}/decisionedNotificationCountsByTransactionTypeAndStatus`;
  urlClearDecisionedNotifications = `${environment.apiUrl}${this.controllerUrl}/clearDecisionedNotifications`;
  urlGrid = `${environment.apiUrl}${this.controllerUrl}/grid`;
  urlGridExportCSV = `${environment.apiUrl}${this.controllerUrl}/gridExportCSV`;
  urlGridExportExcel = `${environment.apiUrl}${this.controllerUrl}/gridExportExcel`;
  urlApprove = `${environment.apiUrl}${this.controllerUrl}/approve`;
  urlDeny = `${environment.apiUrl}${this.controllerUrl}/deny`;
  urlRfpApprove = `${environment.apiUrl}${this.controllerUrl}/rfp/approve`;
  urlRfpDeny = `${environment.apiUrl}${this.controllerUrl}/rfp/deny`;
  urlNoDecision = `${environment.apiUrl}${this.controllerUrl}/nodecision`;

  constructor(private http: HttpClient) {
    super();
  }

  tableFilter(filter: ITableServerFilter): Promise<ITableServerResponseFilter> {
    return firstValueFrom(this.http.post<ITableServerResponseFilter>(this.urlGrid, filter));
  }

  tableExportCSV(filter: ITableServerFilter): Promise<Blob> {
    return firstValueFrom(this.http.post(this.urlGridExportCSV, filter, { responseType: 'blob' }));
  }

  tableExportExcel(filter: ITableServerFilter): Promise<any> {
    return firstValueFrom(this.http.post(this.urlGridExportExcel, filter));
  }

  /**
   * Retrieves the count of unfinished FedNow transaction dual approvals from the backend API.
   * This includes pending approvals as well as those that are in the process of being reviewed.
   * @param options - Optional request configuration.
   * @param options.skipLoading - When `true`, sets the `Skip-Loading` header to bypass the global loading indicator.
   * @returns A promise resolving to the unfinished dual approval count.
   */
  getUnfinishedFedNowTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<IUnfinishedDualApprovalCount> {
    let headers = new HttpHeaders();

    if (options?.skipLoading) {
      headers = headers.set('Skip-Loading', 'true');
    }

    return firstValueFrom(
      this.http.get<IUnfinishedDualApprovalCount>(
        this.urlGetUnfinishedFedNowTransactionApprovalCount,
        {
          headers,
        }
      )
    );
  }

  getUnfinishedRtpTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<IUnfinishedDualApprovalCount> {
    let headers = new HttpHeaders();

    if (options?.skipLoading) {
      headers = headers.set('Skip-Loading', 'true');
    }

    return firstValueFrom(
      this.http.get<IUnfinishedDualApprovalCount>(
        this.urlGetUnfinishedRtpTransactionApprovalCount,
        { headers }
      )
    );
  }

  /**
   * Retrieves the count of pending FedNow transaction dual approvals from the backend API.
   * This includes approvals that have not yet been picked up for review (or were picked
   * up for review but then added back to the pending queue without a decision made).
   * @param options - Optional request configuration.
   * @param options.skipLoading - When `true`, sets the `Skip-Loading` header to bypass the global loading indicator.
   * @returns A promise resolving to the pending dual approval count.
   */
  getPendingFedNowTransactionApprovalCount(options?: {
    skipLoading?: boolean;
  }): Promise<IPendingDualApprovalCount> {
    let headers = new HttpHeaders();

    if (options?.skipLoading) {
      headers = headers.set('Skip-Loading', 'true');
    }

    return firstValueFrom(
      this.http.get<IPendingDualApprovalCount>(this.urlGetPendingFedNowTransactionApprovalCount, {
        headers,
      })
    );
  }

  /**
   * Retrieves decisioned notification counts for the current user, grouped by approval status.
   * @param options - Optional request configuration.
   * @param options.skipLoading - When `true`, sets the `Skip-Loading` header to bypass the global loading indicator.
   * @returns A promise resolving to the per-status decisioned notification counts.
   */
  getDecisionedNotificationCountsByStatus(options?: {
    skipLoading?: boolean;
    transactionType?: string;
  }): Promise<IDecisionedNotificationCount[]> {
    let headers = new HttpHeaders();
    const params: Record<string, string> = {};

    if (options?.skipLoading) {
      headers = headers.set('Skip-Loading', 'true');
    }

    if (options?.transactionType) {
      params['TransactionType'] = options.transactionType;
    }

    return firstValueFrom(
      this.http.get<IDecisionedNotificationCount[]>(this.urlDecisionedNotificationCountsByStatus, {
        headers,
        params,
      })
    );
  }

  /**
   * Retrieves decisioned notification counts grouped by transaction type and approval status
   * for the current user.
   * @param options - Optional request configuration.
   * @param options.skipLoading - When `true`, sets the `Skip-Loading` header to bypass the global loading indicator.
   * @returns A promise resolving to the grouped decisioned notification counts.
   */
  getDecisionedNotificationCountsByTransactionType(options?: {
    skipLoading?: boolean;
  }): Promise<IDecisionedNotificationCountByTransactionType[]> {
    let headers = new HttpHeaders();

    if (options?.skipLoading) {
      headers = headers.set('Skip-Loading', 'true');
    }

    return firstValueFrom(
      this.http.get<IDecisionedNotificationCountByTransactionType[]>(
        this.urlDecisionedNotificationCountsByTransactionType,
        {
          headers,
        }
      )
    );
  }

  /**
   * Clears all pending decisioned notifications for the current user.
   * @returns A promise resolving to the backend operation result.
   */
  clearDecisionedNotifications(options?: {
    skipLoading?: boolean;
    transactionType?: string;
  }): Promise<IWebMessage> {
    let headers = new HttpHeaders();
    const params: Record<string, string> = {};

    if (options?.skipLoading) {
      headers = headers.set('Skip-Loading', 'true');
    }

    if (options?.transactionType) {
      params['TransactionType'] = options.transactionType;
    }

    return firstValueFrom(
      this.http.post<IWebMessage>(this.urlClearDecisionedNotifications, {}, { headers, params })
    );
  }

  /**
   * Retrieves the full details of a single FedNow Transaction Dual Approval Summary entry
   * from the backend API.
   * @param id - The ID of the dual approval summary entry.
   * @returns A promise resolving to the dual approval summary.
   */
  getSummary(id: number): Promise<IFedNowTransactionDualApprovalSummary> {
    return firstValueFrom(
      this.http.get<IFedNowTransactionDualApprovalSummary>(
        `${environment.apiUrl}${this.controllerUrl}/summary/${id}`
      )
    );
  }

  /**
   * Approves a FedNow Transaction Dual Approval entry via the backend API.
   * @param request - The decision request containing the ID, action, and optional comment.
   */
  approve(request: IDualApprovalDecision): Promise<IWebMessage> {
    return firstValueFrom(this.http.post<IWebMessage>(`${this.urlApprove}`, request));
  }

  /**
   * Denies a FedNow Transaction Dual Approval entry via the backend API.
   * @param request - The decision request containing the ID, action, and required comment.
   */
  deny(request: IDualApprovalDecision): Promise<IWebMessage> {
    return firstValueFrom(this.http.post<IWebMessage>(`${this.urlDeny}`, request));
  }

  /**
   * Approves a FedNow RFP Decision dual approval entry via the backend API.
   * @param request - The decision request containing the ID and optional comment.
   */
  rfpApprove(request: IDualApprovalDecision): Promise<IWebMessage> {
    return firstValueFrom(this.http.post<IWebMessage>(`${this.urlRfpApprove}`, request));
  }

  /**
   * Denies a FedNow RFP Decision dual approval entry via the backend API.
   * @param request - The decision request containing the ID, required comment, and reason code.
   */
  rfpDeny(request: IDualApprovalDecision): Promise<IWebMessage> {
    return firstValueFrom(this.http.post<IWebMessage>(`${this.urlRfpDeny}`, request));
  }

  /**
   * Sends a no-decision close action for a dual approval summary entry.
   * @param dualSummaryId - The dual approval summary identifier.
   */
  noDecision(dualSummaryId: number): Promise<IWebMessage> {
    return firstValueFrom(
      this.http.post<IWebMessage>(this.urlNoDecision, { dualsummaryid: dualSummaryId })
    );
  }

  /**
   * Determines whether dual approval is required for a FedNow transaction
   * with the specified details by calling the backend API.
   * @param request - The request containing the transaction amount to evaluate.
   * @returns A promise resolving to whether dual approval is required.
   */
  isDualApprovalRequired(
    request: IDualApprovalRequiredRequest
  ): Promise<IDualApprovalRequiredResponse> {
    return firstValueFrom(
      this.http.post<IDualApprovalRequiredResponse>(
        `${environment.apiUrl}${this.controllerUrl}/isDualApprovalRequired`,
        request
      )
    );
  }
}
